import * as React from 'react';

import { TicketDTO, Permission, Res, TicketLocationDTO, TicketSummaryDTO } from 'core/api';
import { handleServerErrors, Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { createSaleCentersContent, createMapsContent, SaleLocationStatus, PosStatelessScreenClassComponent, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import * as Images from 'pos/Images';
import selectTicket from 'pos/modules/helpers/ticket-selection/selectTicket';
import { confirmMovement } from './confirmMovement';
import { userCanClaimTicket } from 'pos/modules/helpers/ticket-selection';
import { TicketSelection } from 'pos/modules/helpers/ticket-selection/SelectTicketScreen.Common';
import { OrderingFacade, RestaurantFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Message, Modal } from 'core/ui-next/modal';

const LocationStatus = SaleLocationStatus.create(RestaurantFacade.getRestaurantStatus);
const SaleCentersContent = createSaleCentersContent(LocationStatus);
const MapsContent = createMapsContent(LocationStatus);

type Props = {
	ticket: TicketDTO,
	origin?: TicketDTO
} & Modal.Actions<TicketDTO>;

const canBeJoined = (sourceTicket: TicketDTO, targetTicket: TicketSummaryDTO): boolean =>
	sourceTicket.Id !== targetTicket.Id;

const isSameLocation = (sourceLocation: TicketLocationDTO, targetocation: TicketLocationDTO): boolean =>
	sourceLocation.SaleCenterId === targetocation.SaleCenterId && sourceLocation.SaleLocationName === targetocation.SaleLocationName;

class MoveTicketScreen extends PosStatelessScreenClassComponent<Props> {

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	onComponentDidMount(): void {
		LocationStatus.registerOnChange(async () => this.ifMounted(() => this.forceUpdate()));
	}

	onComponentWillUnmount(): void {
		LocationStatus.unregisterOnChanged();
	}

	private async moveOrJoin(sourceTicket: TicketDTO, selection: TicketSelection, location: TicketLocationDTO) {

		if (selection.select === 'existing') {
			const canClaim = await userCanClaimTicket(selection.ticket);
			if (!canClaim) {
				this.cancel();
				return;
			}
		}

		const confirmResult = await confirmMovement(sourceTicket.Location, location, selection);
		if (!confirmResult.ok) {
			this.cancel();
			return;
		}

		await this.execute({
			action: async () => {
				const result = selection.select === 'new'
					? await safeMove(() => OrderingFacade.moveTicket(sourceTicket, confirmResult.value.keepPrices, location))
					: await safeMove(() => OrderingFacade.joinTicket(sourceTicket, confirmResult.value.keepPrices, location, selection.ticket!.Id));

				if (!result.ok) {
					this.cancel();
					return;
				}

				this.close(result.value);
			}
		});
	}

	private onSaleLocationSelected = async (location: TicketLocationDTO) => {

		const tickets = (await OrderingFacade.getExistingTicketsIn(location.SaleCenterId, location.SaleLocationName))
			.filter(t => canBeJoined(this.props.ticket, t) && t.Id !== (this.props.origin ? this.props.origin.Id : 0));

		if (isSameLocation(this.props.ticket.Location, location) && tickets.length === 0) {
			await Message.show(Res.Msg.MSG_MUST_SELECT_A_LOCATION_DIFFERENT_FROM_THE_CURRENT_LOCATION());
			return;
		}

		if (tickets.length === 0) {
			await this.moveOrJoin(this.props.ticket, { select: 'new' }, location);
			return;
		}

		const selectionResult = await selectTicket({ tickets }, 'show-create-new-command', 'hide-ticket-commands');
		if (!selectionResult.ok) {
			this.cancel();
			return;
		}

		await this.moveOrJoin(this.props.ticket, selectionResult.value, location);

	};

	protected getScreenId(): string {
		return 'MOVE_TICKET';
	}

	protected getTitle(): string {
		return Res.Web.MOVE_TICKET();
	}

	protected getImage(): string | SvgIcon {
		return Images.MoveTicket;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const mode = LocationStatus.getSelection().mode === 'maps'
			? { title: Res.Web.SELECT_SALE_LOCATION_SALE_CENTER_BTN(), image: Images.SaleCenter, action: LocationStatus.selectSaleCenter }
			: LocationStatus.getAllMaps().length ? { title: Res.Web.SELECT_SALE_LOCATION_MAPS_BTN(), image: Images.Map, action: LocationStatus.selectMap } : null;

		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			mode
		];
	}

	protected getContent(): React.ReactNode {

		const selection = LocationStatus.getSelection();

		return <>
			{selection.mode === 'maps'
				? <MapsContent selected={selection.selectedMap} onSaleLocationSelected={this.onSaleLocationSelected} />
				: <SaleCentersContent selected={selection.selectedSaleCenter} onSaleLocationSelected={this.onSaleLocationSelected} />}
		</>;
	}
}

async function safeMove<T>(fn: () => Promise<T>): Promise<Result<T>> {

	const result = await handleServerErrors(fn, {
		onEmptyTicketError: async _error => {
			await Message.show(Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY());
		},
		onDomainError: async error => {
			await Message.show(error.message);
		},
		onTicketNotFoundError: async _error => {
			await Message.show(Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER());
		}
	});

	return result;
}


declare global {
	interface CommandType {
		moveTicket: (ticket: TicketDTO, origin?: TicketDTO) => Promise<Result<TicketDTO>>
	}
}

Command.register('moveTicket', Preconditions.ensure(Permission.Ticket_MoveTicket), async (ticket: TicketDTO, origin?: TicketDTO): Promise<Result<TicketDTO>> => {
	if (!ticket.IsInUse) {
		await Message.show(Res.Msg.MSG_TICKET_EMPTY_AND_WITHOUT_COMPANIONS());
		return Result.error();
	}

	await LocationStatus.initialize();

	return await Modal.show(MoveTicketScreen, { ticket, origin });
});